import { Box, Group, Stack, useMantineTheme } from '@mantine/core';
import type { EmbeddedPromptParentOut } from '@repo/api-codegen';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Text, Title } from '@repo/foundations';
import { buildResourceUrl } from '../../../../utils/navigationUtils';
import { AgentMessage } from '../../../AiChatPreviewModal/components/AgentMessage';
import { UserMessage } from '../../../AiChatPreviewModal/components/UserMessage';
import type { EnhancedNotification } from '../../types';

interface IResourceAiAgentContentProps {
	selected: EnhancedNotification;
	embeddedPrompt?: EmbeddedPromptParentOut | null;
	isLoadingEmbeddedPrompt?: boolean;
}

export function ResourceAiAgentContent({
	selected,
	embeddedPrompt,
	isLoadingEmbeddedPrompt,
}: IResourceAiAgentContentProps) {
	const navigate = useNavigate();
	const theme = useMantineTheme();

	const taskTitle = selected.resource_title || 'Untitled AI Task';
	const embeddedPromptId = selected.metadata?.embedded_prompt_id as string;

	const handleNavigateToResource = () => {
		if (selected.entity) {
			navigate(buildResourceUrl(selected.entity));
		}
	};

	return (
		<Stack spacing="xl">
			{/* Title Header */}
			{taskTitle && (
				<Group
					onClick={handleNavigateToResource}
					pt="xl"
					pb="xs"
					spacing="xs"
					sx={{ cursor: selected.entity ? 'pointer' : 'default' }}
				>
					<Title size="xl">{taskTitle}</Title>
				</Group>
			)}

			{/* AI Conversation Section */}
			{embeddedPromptId ? (
				<Stack spacing="sm">
					<Box>
						{isLoadingEmbeddedPrompt && (
							<Text color="text/secondary/default">
								Loading conversation...
							</Text>
						)}
						{!isLoadingEmbeddedPrompt && embeddedPrompt && (
							<Stack spacing="md">
								<UserMessage prompt={embeddedPrompt} />
								<AgentMessage prompt={embeddedPrompt} />
							</Stack>
						)}
						{!isLoadingEmbeddedPrompt && !embeddedPrompt && (
							<Text color="text/secondary/default">
								Conversation not available
							</Text>
						)}
					</Box>
				</Stack>
			) : (
				/* Fallback to basic content if no embedded prompt */
				selected.content && (
					<Stack spacing="sm">
						<Text size="sm" color="text/secondary/default">
							Content
						</Text>
						<Box
							style={{
								border: `1px solid ${theme.other.getColor('border/primary/default')}`,
								borderRadius: theme.radius.sm,
								padding: theme.spacing.md,
								backgroundColor: theme.other.getColor(
									'surface/primary/default'
								),
								whiteSpace: 'pre-wrap',
							}}
						>
							<Text>{selected.content}</Text>
						</Box>
					</Stack>
				)
			)}
		</Stack>
	);
}
